import * as React from "react";

import { BindingPath } from "../../Engine/Core/CoreTypes";
import { IForm } from "../../Engine/Form/IForm";
import { IUpdateFormModelAccessor } from "../../Engine/Form/IFormModelAccessor";

export const CustomControlModelAccessorContext = React.createContext<ICustomControlModelAccessor | undefined>(
    undefined
);

interface ICustomControlUpdateModelAccessor {
    getValue(bindingPath: BindingPath): string | undefined;
    setValue(bindingPath: BindingPath, value: string): void;
}

export class CustomControlUpdateModelAccessor implements ICustomControlUpdateModelAccessor {
    readonly #modelAccessor: IUpdateFormModelAccessor;

    public constructor(modelAccessor: IUpdateFormModelAccessor) {
        this.#modelAccessor = modelAccessor;
    }

    public getValue(bindingPath: BindingPath): string | undefined {
        return this.#modelAccessor.getValue(bindingPath);
    }

    public setValue(bindingPath: BindingPath, value: string): void {
        this.#modelAccessor.setValue(bindingPath, value);
    }
}

export interface ICustomControlModelAccessor {
    update(batchUpdateCallback: (model: ICustomControlUpdateModelAccessor) => void): void;
    resolveMask(pathWithMask: BindingPath): BindingPath[];
}

export class CustomControlModelAccessor implements ICustomControlModelAccessor {
    readonly #form: IForm;

    public constructor(form: IForm) {
        this.#form = form;
    }

    public update(batchUpdateCallback: (model: ICustomControlUpdateModelAccessor) => void): void {
        this.#form.updateModel(modelAccessor => {
            const customControlUpdateModelAccessor = new CustomControlUpdateModelAccessor(modelAccessor);
            batchUpdateCallback(customControlUpdateModelAccessor);
        });
    }

    public resolveMask(pathWithMask: BindingPath): BindingPath[] {
        return this.#form.resolveMask(pathWithMask);
    }
}

export function withModelAccessor<T>(
    Component: React.ComponentType<T & { modelAccessor: ICustomControlModelAccessor }>
): React.ComponentType<T> {
    return function WithModelAccessorWrapper(props: T): JSX.Element {
        const modelAccessor = React.useContext(CustomControlModelAccessorContext);
        if (modelAccessor == undefined) {
            throw new Error("modelAccessor must be provided");
        }
        return <Component {...props} modelAccessor={modelAccessor} />;
    };
}
